/**
 * Peer Evaluation Print Page
 *
 * Print-optimized page for peer evaluation feedback.
 * Opens in new tab and triggers print dialog automatically.
 */

import { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePeerEvaluation } from './usePeerEvaluations';
import { PrintLayout } from '../../components/print';

/**
 * Format datum naar nl-NL locale string
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return 'Onbekende datum';
  }
  return date.toLocaleDateString('nl-NL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Score labels voor weergave
 */
const SCORE_LABELS: Record<number, string> = {
  1: 'Onvoldoende',
  2: 'Matig',
  3: 'Voldoende',
  4: 'Goed',
  5: 'Uitzonderlijk',
};

/**
 * Score kleuren voor weergave
 */
const SCORE_COLORS: Record<number, string> = {
  1: 'bg-red-100 text-red-800 border-red-300',
  2: 'bg-orange-100 text-orange-800 border-orange-300',
  3: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  4: 'bg-lime-100 text-lime-800 border-lime-300',
  5: 'bg-green-100 text-green-800 border-green-300',
};

/**
 * Status labels
 */
const STATUS_LABELS = {
  concept: 'Concept',
  ingediend: 'Ingediend',
} as const;

export function PeerEvaluationPrintPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const peerEvalId = useMemo(() => {
    const parsed = parseInt(id ?? '', 10);
    return isNaN(parsed) ? 0 : parsed;
  }, [id]);

  const { data: peerEvaluation, isLoading, error } = usePeerEvaluation(peerEvalId);

  const errorMessage = error instanceof Error ? error.message : 'Peer evaluatie niet gevonden';

  if (error || (!isLoading && !peerEvaluation)) {
    return (
      <PrintLayout
        title="Peer Evaluatie"
        error={errorMessage}
        onClose={() => navigate(-1)}
      >
        <></>
      </PrintLayout>
    );
  }

  if (isLoading || !peerEvaluation) {
    return (
      <PrintLayout
        title="Peer Evaluatie"
        isLoading={true}
        loadingMessage="Peer evaluatie voorbereiden..."
      >
        <></>
      </PrintLayout>
    );
  }

  // Format print date
  const printDate = new Date().toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <PrintLayout
      title={`Peer Evaluatie #${peerEvaluation.id}`}
      subtitle={`Evaluator: ${peerEvaluation.evaluatorName} - ${printDate}`}
      onClose={() => navigate(-1)}
      autoPrint={true}
      autoPrintDelay={500}
    >
      {/* Meta informatie */}
      <div className="print-card bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Evaluator:</span>{' '}
            <span className="font-medium text-gray-900">{peerEvaluation.evaluatorName}</span>
          </div>
          <div>
            <span className="text-gray-500">Evaluatie:</span>{' '}
            <span className="font-medium text-gray-900">#{peerEvaluation.id}</span>
          </div>
          <div>
            <span className="text-gray-500">Status:</span>{' '}
            <span className="font-medium text-gray-900">{STATUS_LABELS[peerEvaluation.status]}</span>
          </div>
          <div>
            <span className="text-gray-500">Aangemaakt:</span>{' '}
            <span className="font-medium text-gray-900">{formatDate(peerEvaluation.createdAt)}</span>
          </div>
          {peerEvaluation.submittedAt && (
            <div>
              <span className="text-gray-500">Ingediend:</span>{' '}
              <span className="font-medium text-gray-900">{formatDate(peerEvaluation.submittedAt)}</span>
            </div>
          )}
          <div>
            <span className="text-gray-500">Aangemaakt door:</span>{' '}
            <span className="font-medium text-gray-900">{peerEvaluation.createdByName}</span>
          </div>
        </div>
      </div>

      {/* Taken sectie */}
      {peerEvaluation.tasks.length > 0 && (
        <div className="print-section mb-6">
          <h2 className="print-section-header text-lg font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-300">
            Taken ({peerEvaluation.tasks.length})
          </h2>
          <div className="space-y-4">
            {peerEvaluation.tasks.map((task) => (
              <div
                key={task.id}
                className="print-card border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">{task.name}</h3>
                    {task.beschrijving && (
                      <p className="text-sm text-gray-500 mt-1">{task.beschrijving}</p>
                    )}
                  </div>
                  {task.score !== null && (
                    <div
                      className={`px-3 py-1 rounded-lg border text-sm font-medium ${
                        SCORE_COLORS[task.score] ?? 'bg-gray-100 text-gray-800 border-gray-300'
                      }`}
                    >
                      {task.score} - {SCORE_LABELS[task.score] ?? ''}
                    </div>
                  )}
                </div>
                {task.remarks && (
                  <div className="mt-3 bg-gray-50 rounded p-3">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Opmerkingen</p>
                    <p className="text-sm text-gray-900">{task.remarks}</p>
                  </div>
                )}
                {!task.remarks && task.score === null && (
                  <div className="mt-3 bg-gray-50 rounded p-3">
                    <p className="text-sm text-gray-400 italic">Geen feedback gegeven</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Competenties sectie */}
      {peerEvaluation.competencies.length > 0 && (
        <div className="print-section mb-6">
          <h2 className="print-section-header text-lg font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-300">
            Competenties ({peerEvaluation.competencies.length})
          </h2>
          <div className="space-y-4">
            {peerEvaluation.competencies.map((comp) => (
              <div
                key={comp.id}
                className="print-card border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-gray-900">{comp.name}</h3>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          comp.type === 'basis'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-purple-100 text-purple-800'
                        }`}
                      >
                        {comp.type === 'basis' ? 'Basis' : 'Vak'}
                      </span>
                    </div>
                    {comp.beschrijving && (
                      <p className="text-sm text-gray-500 mt-1">{comp.beschrijving}</p>
                    )}
                  </div>
                  {comp.score !== null && (
                    <div
                      className={`px-3 py-1 rounded-lg border text-sm font-medium ${
                        SCORE_COLORS[comp.score] ?? 'bg-gray-100 text-gray-800 border-gray-300'
                      }`}
                    >
                      {comp.score} - {SCORE_LABELS[comp.score] ?? ''}
                    </div>
                  )}
                </div>
                {comp.remarks && (
                  <div className="mt-3 bg-gray-50 rounded p-3">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Opmerkingen</p>
                    <p className="text-sm text-gray-900">{comp.remarks}</p>
                  </div>
                )}
                {!comp.remarks && comp.score === null && (
                  <div className="mt-3 bg-gray-50 rounded p-3">
                    <p className="text-sm text-gray-400 italic">Geen feedback gegeven</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Algemene opmerkingen sectie */}
      <div className="print-section">
        <h2 className="print-section-header text-lg font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-300">
          Algemene Opmerkingen
        </h2>
        <div className="print-card border border-gray-200 rounded-lg p-4 bg-gray-50">
          <p className={`text-sm ${peerEvaluation.generalRemarks ? 'text-gray-900' : 'text-gray-400 italic'}`}>
            {peerEvaluation.generalRemarks || 'Geen algemene opmerkingen'}
          </p>
        </div>
      </div>
    </PrintLayout>
  );
}
